import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useOpportunityWizard } from './OpportunityWizardContext';
import OpportunityWizardLayout from './OpportunityWizardLayout';
import { toast } from 'sonner';
import { Loader2, Plus, Trash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

// Define interface that matches both our local needs and the database structure
interface Contact {
  id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone: string | null;
  customer_id: string;  // Match database field
}

interface LocalSelectedContact {
  contact_id: string;
  contact_type: 'technical' | 'administrative' | 'billing' | 'pedagogical';
  contact: Contact;
}

const Step3Contacts = () => {
  const navigate = useNavigate();
  const { state, setOpportunityContacts } = useOpportunityWizard();
  
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  // Cast to our local type for component use
  const [selectedContacts, setSelectedContacts] = useState<LocalSelectedContact[]>(
    state.selectedContacts.map(sc => ({
      ...sc,
      contact: {
        ...sc.contact,
        customer_id: sc.contact.customer_id || ''
      }
    })) as LocalSelectedContact[]
  );
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    contact_type: 'technical' as 'technical' | 'administrative' | 'billing' | 'pedagogical'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Redirecionar se nenhum cliente estiver selecionado
  useEffect(() => {
    if (!state.selectedCustomer) {
      navigate('../step1', { relative: 'path' });
    }
  }, [state.selectedCustomer, navigate]);

  useEffect(() => {
    fetchContacts();
  }, [state.selectedCustomer]);

  const fetchContacts = async () => {
    if (!state.selectedCustomer) return;
    
    try {
      setLoading(true);
      
      // Fetch all contacts associated with the customer
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', state.selectedCustomer.id);
      
      if (error) throw error;
      
      setContacts(data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Falha ao carregar contatos');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Convert back to the context expected type
    const contextContacts = selectedContacts.map(sc => ({
      contact_id: sc.contact_id,
      contact_type: sc.contact_type,
      contact: {
        id: sc.contact.id,
        first_name: sc.contact.first_name,
        last_name: sc.contact.last_name,
        email: sc.contact.email,
        phone: sc.contact.phone,
        customer_id: sc.contact.customer_id
      }
    }));
    
    setOpportunityContacts(contextContacts);
    navigate('../step4', { relative: 'path' });
  };

  const handleCreateContact = async () => {
    if (!state.selectedCustomer) return;
    
    // Validação
    const errors: Record<string, string> = {};
    
    if (!newContact.first_name.trim()) {
      errors.first_name = 'Nome é obrigatório';
    }
    
    if (!newContact.email.trim()) {
      errors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(newContact.email)) {
      errors.email = 'Email inválido';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);

    try {
      // Create the contact directly with customer_id
      const { data, error } = await supabase
        .from('customer_contacts')
        .insert({
          customer_id: state.selectedCustomer.id,
          first_name: newContact.first_name,
          last_name: newContact.last_name || null,
          email: newContact.email,
          phone: newContact.phone || null
        })
        .select()
        .single();

      if (error) throw error;

      const newContacts = [...contacts, data];
      setContacts(newContacts);
      
      // Adicionar à lista de contatos selecionados
      const newSelectedContact: LocalSelectedContact = {
        contact_id: data.id,
        contact_type: newContact.contact_type,
        contact: data
      };
      
      setSelectedContacts([newSelectedContact]);
      
      // Reset form
      setNewContact({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        contact_type: 'technical'
      });
      
      setIsCreateDialogOpen(false);
      toast.success('Contato adicionado com sucesso');
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Erro ao criar contato');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectContact = (contact: Contact, contactType: 'technical' | 'administrative' | 'billing' | 'pedagogical') => {
    // Selecionar apenas um contato, substituindo qualquer seleção anterior
    setSelectedContacts([{
      contact_id: contact.id,
      contact_type: contactType,
      contact
    }]);
  };

  const handleRemoveContact = (index: number) => {
    setSelectedContacts([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewContact({...newContact, [name]: value});
    
    // Limpar erro quando o usuário começa a digitar
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const isContactSelected = (contactId: string, type: 'technical' | 'administrative' | 'billing' | 'pedagogical') => {
    return selectedContacts.some(
      sc => sc.contact_id === contactId && sc.contact_type === type
    );
  };

  return (
    <OpportunityWizardLayout
      currentStep={3}
      totalSteps={7}
      title="Selecionar Contato"
      description="Selecione um contato para a oportunidade"
      onNext={handleNext}
      onPrevious={() => navigate('../step2', { relative: 'path' })}
      isNextDisabled={false}
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-extrabold">
              Cliente: {state.selectedCustomer?.name}
            </h2>
            {state.selectedCustomer?.legal_name && (
              <p className="text-sm text-muted-foreground">
                {state.selectedCustomer.legal_name}
              </p>
            )}
          </div>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Contato
          </Button>
        </div>

        {/* Lista de contatos selecionados */}
        <div className="space-y-3">
          <h3 className="text-md font-extrabold">Contatos selecionados</h3>
          
          {selectedContacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum contato selecionado. Selecione um contato abaixo.
            </p>
          ) : (
            <div className="grid gap-3">
              {selectedContacts.map((selectedContact, index) => (
                <Card key={`${selectedContact.contact_id}-${selectedContact.contact_type}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-extrabold">
                            {selectedContact.contact.first_name} {selectedContact.contact.last_name}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {selectedContact.contact.email}
                        </div>
                        {selectedContact.contact.phone && (
                          <div className="text-sm text-muted-foreground">
                            {selectedContact.contact.phone}
                          </div>
                        )}
                      </div>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleRemoveContact(index)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Lista de contatos disponíveis */}
        <div className="space-y-3">
          <h3 className="text-md font-extrabold">Contatos disponíveis</h3>
          
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">Não há contatos cadastrados para este tenant</p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Novo Contato
              </Button>
            </div>
          ) : (
            <div className="grid gap-3">
              {contacts.map(contact => (
                <Card key={contact.id}>
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row justify-between gap-4">
                      <div>
                        <h3 className="font-extrabold">
                          {contact.first_name} {contact.last_name}
                        </h3>
                        <div className="text-sm text-muted-foreground">
                          {contact.email}
                        </div>
                        {contact.phone && (
                          <div className="text-sm text-muted-foreground">
                            {contact.phone}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant={isContactSelected(contact.id, 'technical') ? "default" : "outline"}
                          className="text-xs"
                          onClick={() => handleSelectContact(contact, 'technical')}
                        >
                          {isContactSelected(contact.id, 'technical') ? '✓ Selecionado' : 'Selecionar contato'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de criação de contato */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Novo Contato</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">Nome *</Label>
                <Input 
                  id="first_name" 
                  name="first_name"
                  value={newContact.first_name}
                  onChange={handleInputChange}
                  className={formErrors.first_name ? "border-red-500" : ""}
                />
                {formErrors.first_name && <p className="text-red-500 text-xs">{formErrors.first_name}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="last_name">Sobrenome</Label>
                <Input 
                  id="last_name" 
                  name="last_name"
                  value={newContact.last_name}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input 
                  id="email" 
                  name="email"
                  type="email"
                  value={newContact.email}
                  onChange={handleInputChange}
                  className={formErrors.email ? "border-red-500" : ""}
                />
                {formErrors.email && <p className="text-red-500 text-xs">{formErrors.email}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone</Label>
                <Input 
                  id="phone" 
                  name="phone"
                  value={newContact.phone}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Tipo de contato</Label>
              <div className="py-2">
                <Label>Contato técnico</Label>
                <input type="hidden" value="technical" name="contact_type" />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsCreateDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateContact}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </OpportunityWizardLayout>
  );
};

export default Step3Contacts; 